/**
 * hrmsea product UI tokens (mobile).
 * Employer name on payslips comes from the API (`company.name`), not these tokens.
 */

export const brand = {
  name: 'hrma',
  tagline: 'Malaysian HRMS',
} as const;

export const page = {
  stack: 'space-y-5',
  title: 'text-xl font-semibold tracking-tight text-neutral-900',
  subtitle: 'text-sm text-neutral-500',
  sectionTitle: 'text-sm font-semibold text-neutral-800',
} as const;

/** Flat cards — subtle elevation on interactive surfaces */
export const card = {
  base: 'rounded-xl border border-neutral-200/90 bg-white shadow-soft',
  padding: 'p-4',
  interactive:
    'transition-all duration-200 hover:border-primary-200 hover:shadow-elevated active:scale-[0.98]',
  full: 'rounded-xl border border-neutral-200/90 bg-white p-4 shadow-soft',
  elevated: 'rounded-xl border border-neutral-200/90 bg-white p-4 shadow-elevated',
} as const;

export const hero = {
  wrap: 'rounded-2xl bg-gradient-to-br from-primary-600 to-primary-800 p-5 text-white shadow-elevated',
  title: 'text-lg font-semibold',
  subtitle: 'text-sm text-primary-100',
  meta: 'text-xs text-primary-200/90',
} as const;

export const stat = {
  card: 'rounded-xl border border-neutral-200/90 bg-white p-4 shadow-soft',
  label: 'text-xs font-medium uppercase tracking-wide text-neutral-500',
  value: 'mt-1 text-2xl font-bold tabular-nums text-neutral-900',
  hint: 'mt-0.5 text-xs text-neutral-500',
} as const;

export const badge = {
  base: 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
  neutral: 'bg-neutral-100 text-neutral-700',
  primary: 'bg-primary-50 text-primary-700',
  success: 'bg-emerald-50 text-emerald-700',
  warning: 'bg-amber-50 text-amber-800',
  danger: 'bg-red-50 text-red-700',
  info: 'bg-sky-50 text-sky-700',
} as const;

export const input = {
  label: 'mb-1.5 block text-sm font-medium text-neutral-700',
  field:
    'w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-neutral-900 shadow-soft transition-colors placeholder:text-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20',
  select:
    'w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-neutral-900 shadow-soft focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20',
} as const;

export const skeleton = {
  line: 'h-4 animate-pulse rounded-md bg-neutral-200/80',
  block: 'animate-pulse rounded-xl bg-neutral-200/80',
  card: 'h-24 animate-pulse rounded-xl bg-neutral-200/60',
} as const;

export const listItem = {
  wrap: 'rounded-xl border border-neutral-200/90 bg-white p-4 shadow-soft transition-colors',
  title: 'font-medium text-neutral-900',
  meta: 'text-sm text-neutral-600',
  caption: 'text-xs text-neutral-400',
} as const;

export const button = {
  primary:
    'inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-soft transition-colors hover:bg-primary-700 active:bg-primary-800 disabled:opacity-50',
  primaryLg:
    'inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white shadow-elevated transition-colors hover:bg-primary-700 active:bg-primary-800 disabled:opacity-50',
  secondary:
    'inline-flex items-center justify-center gap-2 rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm font-medium text-neutral-700 shadow-soft transition-colors hover:bg-neutral-50 active:bg-neutral-100',
  danger:
    'inline-flex w-full items-center justify-center gap-2 rounded-xl bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white shadow-soft transition-colors hover:bg-amber-700 disabled:opacity-50',
  link: 'inline-flex items-center gap-1 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700',
  ghost: 'rounded-lg px-2 py-1 text-sm text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900',
  icon: 'inline-flex h-9 w-9 items-center justify-center rounded-full text-neutral-600 transition-colors hover:bg-neutral-100',
} as const;

export const chrome = {
  shell: 'flex min-h-dvh flex-col bg-[hsl(var(--background))]',
  header:
    'sticky top-0 z-10 shrink-0 border-b border-neutral-200/80 bg-white/90 backdrop-blur-md supports-[backdrop-filter]:bg-white/75 pt-safe shadow-soft',
  headerInner: 'flex min-h-[3.5rem] items-center justify-between gap-3 px-4 py-2',
  headerTitle: 'truncate text-base font-semibold tracking-tight text-neutral-900',
  headerMeta: 'max-w-[6rem] truncate text-xs text-neutral-500 sm:max-w-[8rem] sm:text-sm',
  headerLogout:
    'shrink-0 rounded-lg border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-600 transition-colors hover:border-neutral-300 hover:bg-neutral-50',
  avatar:
    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700',
  main: 'min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-4 py-4 pb-tabbar',
  bottomNav:
    'fixed bottom-0 left-0 right-0 z-20 border-t border-neutral-200/80 bg-white/95 backdrop-blur-md shadow-[0_-4px_24px_rgba(0,0,0,0.06)]',
  bottomNavInner:
    'flex min-h-[var(--app-tabbar-height)] items-stretch justify-around gap-0.5 px-2 pt-1.5 pb-safe',
  bottomNavActive: 'text-primary-600',
  bottomNavInactive: 'text-neutral-400',
  bottomNavItem:
    'relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-1.5 text-[10px] font-medium leading-tight transition-colors sm:text-[11px]',
  bottomNavItemActive: 'bg-primary-50 text-primary-600',
} as const;

export const alert = {
  error: 'rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700',
  success: 'rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700',
  info: 'rounded-xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-800',
  warning: 'rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800',
} as const;

export const quickAction = {
  wrap: `${card.full} ${card.interactive} flex flex-col items-center gap-2 py-5 text-center`,
  icon: 'flex h-11 w-11 items-center justify-center rounded-2xl',
  label: 'text-sm font-semibold text-neutral-900',
  hint: 'text-xs text-neutral-500',
} as const;
